import cx from 'classnames';
import {useCallback, useEffect, useState} from 'react';
import {shallowEqual, useSelector, useStore} from 'react-redux';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {removeOffer} from '~/CreateFabricInvitation/api/review';
import {eventInEditMode, selectType} from '~/CreateFabricInvitation/reducers/details/selectors';
import {selectIsDraft} from '~/CreateFabricInvitation/reducers/eviteEvent/selectors';
import {
  selectIsPremium,
  selectIsPremiumTemplate,
} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {SET_UPSELL_CONFIRMED} from '~/CreateFabricInvitation/reducers/modals';
import {selectCalc} from '~/CreateFabricInvitation/reducers/review/selectors';
import {EventGiftingWrapper} from '~/CreateFabricInvitation/routes/EventGifting/EventGiftingWrapper';
import {GiftIdeas} from '~/CreateFabricInvitation/routes/InvitationMaker/OneStepCreatePage/components/Options/components/GiftIdeas/GiftIdeas';
import {GIFT_OPTIONS} from '~/CreateFabricInvitation/types/constants';
import {webViewPendingUpgradeToPremium} from '~/CreateFabricInvitation/webview/webview';
import {PledgelingOverlay} from '~/common/fabric/CreateDesign/components/EventGifting/PledgelingOverlay';
import {webViewModalShowing} from '~/common/fabric/hooks';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {toggleOption} from '~/common/fabric/reducers/giftingOptions/actions';
import {
  UPDATE_GIFT_OPTIONS,
  UPDATE_PLEDGELING_OPTIONS,
} from '~/common/fabric/reducers/giftingOptions/constants';
import * as selectors from '~/common/fabric/reducers/giftingOptions/selectors';
import {DowngradeModal} from '~/common/fabric/routes/EventReview/components/DowngradeModal';
import {PLAN_NAME_PARTY_PASS, PLAN_NAME_PRO} from '~/common/fabric/routes/EventReview/constants';
import {UPSELL_MODES, UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {selectUser} from '~/common/fabric/User/reducers/user';
import {isJson} from '~/common/utils';
import {isFabricWebview} from '~/common/utils/webview';
import {useEventSave} from '~/OneStepCreateInvitation/hooks/useEventSave';
import {
  TURN_OFF_DONATION_LOADING,
  TURN_ON_DONATION_LOADING,
} from '~/ViewInvitation/reducers/constants';
import {Typography} from '~library/atoms/Typography';
import {GiftOption} from './components/GiftOption';

import '~/CreateFabricInvitation/routes/EventGifting/EventGifting.sass';
import './Options.sass';

const ADDON_OPTIONS_ORDER = ['host-photo-gallery', 'photo-sharing', 'polls', 'whatToBring'];
export const Options = () => {
  const store = useStore();
  const {autoSave} = useEventSave();
  const [showOverlay, setShowOverlay] = useState('');
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [toToggleOff, setToToggleOff] = useState('');
  const [toToggleOn, setToToggleOn] = useState('');
  const isPremiumTemplate = useSelector(selectIsPremiumTemplate);
  const charityId = useSelector(selectors.selectCharityID);
  const purpose = useSelector(selectors.selectCrowdfundPurpose);
  const giftingOptions = useSelector(selectors.selectGiftingOptions, shallowEqual);
  const eventIsPremium = useSelector(selectIsPremium);
  const calc = useSelector(selectCalc, shallowEqual);
  const isDraft = useSelector(selectIsDraft);
  const user = useSelector(selectUser);
  const [upsellMode, setUpsellMode] = useState(UPSELL_MODES.GIFTING);
  const isInEditMode = useSelector(eventInEditMode);
  const isFuneral = useSelector(selectType) === 'funeral_memorial_service';
  const activeGiftOptionsCount = useSelector(selectors.selectGiftOptionsCount);

  const upsellHasBannerImg = [
    UPSELL_MODES.HOST_PHOTO_GALLERY,
    GIFT_OPTIONS.HOST_PHOTO_GALLERY,
  ].includes(upsellMode);
  const upsellUsesAccordion = [
    UPSELL_MODES.HOST_PHOTO_GALLERY,
    GIFT_OPTIONS.HOST_PHOTO_GALLERY,
  ].includes(upsellMode);

  // Effects
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const defaultGiftingOption = searchParams.get('option');
    if (defaultGiftingOption === 'charity-donation' || defaultGiftingOption === 'crowd-funding') {
      setShowOverlay(defaultGiftingOption);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    const listener = async (e) => {
      if (e.origin.includes('pledge') && isJson(e.data)) {
        const {action, data: payload} = JSON.parse(e.data);

        const embedCodes = document.querySelectorAll('.plg-search, .plg-crowd');
        const embedCode = Array.from(embedCodes).find(
          (code) => code.firstElementChild.contentWindow === e.source
        );

        switch (action) {
          case 'updateEvent': {
            // Turn on donation loading spinner
            store.dispatch({type: TURN_ON_DONATION_LOADING});

            store.dispatch({
              type: UPDATE_PLEDGELING_OPTIONS,
              payload,
            });
            await store.dispatch(saveEventThunk(false, true, 5000));
            // Turn off donation loading spinner
            store.dispatch({type: TURN_OFF_DONATION_LOADING});

            break;
          }

          case 'removeEvent': {
            if ([...embedCode.classList].includes('plg-search')) {
              store.dispatch({
                type: UPDATE_PLEDGELING_OPTIONS,
                // biome-ignore lint/style/useNamingConvention: existing error
                payload: {beneficiary_type: 'Organization'},
              });
            } else {
              store.dispatch({
                type: UPDATE_PLEDGELING_OPTIONS,
                // biome-ignore lint/style/useNamingConvention: existing error
                payload: {beneficiary_type: 'Crowdfund'},
              });
            }
            await store.dispatch(saveEventThunk(false, true));

            break;
          }
          default:
            break;
        }
      }
    };

    if (
      eventIsPremium &&
      !isPremiumTemplate &&
      !giftingOptions.showGifting &&
      giftingOptions.upgraded === false
    ) {
      store.dispatch({
        type: UPDATE_GIFT_OPTIONS,
        payload: {
          upgraded: true,
        },
      });

      store.dispatch({
        type: UPDATE_EVENT_DETAILS,
        payload: {isPremium: true},
      });
    }

    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, []);

  const closeOverlay = useCallback(
    async (overlayTitle) => {
      setShowOverlay('');
      // Ensure registry changes are saved on save
      if (overlayTitle === 'gift-registry') {
        await autoSave();
      }
    },
    [autoSave]
  );

  const backClicked = useCallback(() => {
    if (showOverlay) {
      setShowOverlay('');
    }
  }, [showOverlay]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const toggleUpsellModal = useCallback(
    (mode) => {
      setUpsellMode(mode || UPSELL_MODES.GIFTING);
      if (!showUpsellModal) {
        webViewModalShowing('upsell');
      }
      setShowUpsellModal(!showUpsellModal);
    },
    [showUpsellModal, setShowUpsellModal]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const upsellConfirm = useCallback(async () => {
    store.dispatch({
      type: SET_UPSELL_CONFIRMED,
      payload: true,
    });
    store.dispatch({
      type: UPDATE_EVENT_DETAILS,
      payload: {isPremium: true, hideUpsell: true},
    });

    toggleUpsellModal();

    switch (toToggleOff) {
      case 'giftRegistry':
        store.dispatch({
          type: UPDATE_GIFT_OPTIONS,
          payload: {
            // biome-ignore lint/style/useNamingConvention: existing error
            gift_registry_urls: [],
            // biome-ignore lint/style/useNamingConvention: existing error
            gift_registry_name: '',
          },
        });
        break;
      case 'charityDonation':
        store.dispatch({
          type: UPDATE_PLEDGELING_OPTIONS,
          // biome-ignore lint/style/useNamingConvention: existing error
          payload: {beneficiary_type: 'Organization'},
        });
        break;
      case 'crowdFunding':
        store.dispatch({
          type: UPDATE_PLEDGELING_OPTIONS,
          // biome-ignore lint/style/useNamingConvention: existing error
          payload: {beneficiary_type: 'Crowdfund'},
        });
        break;
      case 'giftIdeas':
        store.dispatch(toggleOption(giftingOptions, 'event_option_show_gifting', false, false));
        break;
      case 'photoSharing':
        store.dispatch(toggleOption(giftingOptions, 'event_option_enable_photo_sharing', true));
        break;
      case 'hostPhotoGallery':
        store.dispatch(
          toggleOption(giftingOptions, 'event_option_enable_host_photo_gallery', true)
        );
        break;
      default:
        break;
    }

    store.dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        upgraded: true,
      },
    });

    setToToggleOff('');

    await store.dispatch(saveEventThunk());
    webViewPendingUpgradeToPremium(true);
    window.dataLayer.push({
      event: 'fabric_create_upgrade_gifting',
    });
  }, [store, toggleUpsellModal, toToggleOff, giftingOptions.options]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const downgradeConfirm = useCallback(async () => {
    /** If calc is not empty (has been run), update selectedOffers in calc */
    if (Object.keys(calc).length !== 0) {
      await removeOffer(store);
    }

    switch (toToggleOn) {
      case 'charityDonation':
        setShowOverlay('charity-donation');
        break;
      case 'crowdFunding':
        setShowOverlay('crowd-funding');
        break;
      case 'giftIdeas':
        store.dispatch(toggleOption(giftingOptions, 'event_option_show_gifting', true, true));
        break;
      case 'photoSharing':
        store.dispatch(toggleOption(giftingOptions, 'event_option_enable_photo_sharing', true));
        break;
      case 'host-photo-gallery':
        store.dispatch(
          toggleOption(giftingOptions, 'event_option_enable_host_photo_gallery', true)
        );
        break;
      default:
        break;
    }

    store.dispatch({
      type: SET_UPSELL_CONFIRMED,
      payload: false,
    });

    store.dispatch({
      type: UPDATE_EVENT_DETAILS,
      payload: {isPremium: false},
    });

    store.dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        upgraded: false,
      },
    });

    setShowDowngradeModal(false);
    setToToggleOn('');
    // Don't save here if in edit mode; only save if in create flow
    await store.dispatch(saveEventThunk(!isInEditMode));
    webViewPendingUpgradeToPremium(false);
    window.dataLayer.push({
      event: 'fabric_create_downgrade_gifting',
    });
  }, [calc, giftingOptions.options, isInEditMode, store, toToggleOn]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const toggleDowngradeModal = useCallback(() => {
    setShowDowngradeModal(!showDowngradeModal);
  }, [showDowngradeModal, setShowDowngradeModal]);

  const shouldShowUpsellModal = useCallback(
    () =>
      !isFuneral && // funerals don't get upselled
      !isPremiumTemplate &&
      !eventIsPremium &&
      user?.subscriptionPlanName !== PLAN_NAME_PRO &&
      user?.subscriptionPlanName !== PLAN_NAME_PARTY_PASS &&
      activeGiftOptionsCount === 1,
    [
      isFuneral,
      isPremiumTemplate,
      eventIsPremium,
      user?.subscriptionPlanName,
      activeGiftOptionsCount,
    ]
  );

  return (
    <>
      <div
        className={cx('gifting-options__container', {free: !eventIsPremium})}
        id="gifting-section"
      >
        <EventGiftingWrapper inPreviewStep disableTracking />

        <PledgelingOverlay
          isOpen={!!showOverlay}
          overlayTitle={showOverlay}
          closeOverlay={closeOverlay}
          backClicked={backClicked}
          charityAdded={!!charityId}
          crowdfundAdded={!!purpose}
        />
        {showUpsellModal && !eventIsPremium && (
          <UpsellModal
            noAutoRestore
            toggleUpsellModal={() => toggleUpsellModal()}
            onConfirmAction={upsellConfirm}
            mode={upsellMode}
            closeOverlay={() => {
              webViewPendingUpgradeToPremium(false);
              toggleUpsellModal();
            }}
            className={`upsell-modal--${upsellMode}`}
            bannerImg={upsellHasBannerImg}
            accordionDetails={upsellUsesAccordion}
          />
        )}
        {showDowngradeModal && eventIsPremium && isDraft && (
          <DowngradeModal
            toggleUpsellModal={() => toggleDowngradeModal()}
            confirmDowngrade={downgradeConfirm}
          />
        )}
      </div>

      {!isFabricWebview() && giftingOptions.showGifting && (
        <div className="gifting-options__container">
          <Typography variant="header5" size="large" className="gifting-options__header">
            Find the perfect gift
          </Typography>
          <GiftIdeas
            shouldShowUpsellModal={shouldShowUpsellModal}
            setToToggleOff={setToToggleOff}
            toggleUpsellModal={toggleUpsellModal}
            eventIsPremium={eventIsPremium}
          />
        </div>
      )}

      <div className="gifting-options__container">
        <Typography variant="header5" size="large" className="gifting-options__header">
          Add easy planning tools
        </Typography>
        <div className="gifting-options__sortable">
          {ADDON_OPTIONS_ORDER.map((o) => (
            <GiftOption
              name={o}
              shouldShowUpsellModal={shouldShowUpsellModal}
              setToToggleOff={setToToggleOff}
              setToToggleOn={setToToggleOn}
              toggleUpsellModal={toggleUpsellModal}
              setShowOverlay={setShowOverlay}
              key={o}
            />
          ))}
        </div>
      </div>
    </>
  );
};
